import React, { createContext, useState, useEffect, useRef } from "react";
import axios from 'axios';
import { confirmTotpEnrollment, login, startTotpEnrollment, verifyTotp } from "../auth/authApi";

export const UserContext = createContext();

function UserContextProvider(props) {
	const [ID, setID] = useState(null);
	const [name, setName] = useState(null);
	const [email, setEmail] = useState(null);
	const [role, setRole] = useState(null);
	const [accessToken, setAccessToken] = useState(null);
	const [loginStatus, setLoginStatus] = useState("loading");
	const [mfaChallenge, setMfaChallenge] = useState(null);
	const [mfaEnrollment, setMfaEnrollment] = useState(null);
	const [mfaOptional, setMfaOptional] = useState(null);
	const [authError, setAuthError] = useState(false);
	const heartBeatRef = useRef(null);
	const authVersionRef = useRef(0);
	const refreshRequestRef = useRef(null);
	// let token = localStorage.getItem("token");

	useEffect(() => {
		let active = true;
		const authVersion = authVersionRef.current;
		if (accessToken) getUserStatus(() => active && authVersion === authVersionRef.current);
		return () => { active = false; };
	}, [accessToken]);

	useEffect(() => () => {
		if (heartBeatRef.current) clearTimeout(heartBeatRef.current);
	}, []);
	

	const completeAuthentication = (data) => {
		authVersionRef.current += 1;
		setID(null);
		setRole(null);
		setAccessToken(data.access_token);
		window.localStorage.setItem('refreshToken', data.refresh_token);
		setMfaChallenge(null);
		setMfaEnrollment(null);
		setMfaOptional(null);
		setAuthError(false);
		setLoginStatus("success");
	}

	const processLogin = (email, password) => {
		setLoginStatus("processing");
		setAuthError(false);
		login(email, password)
			.then((res) => {
				if (res.data.status === "mfa_optional") {
					setMfaOptional(res.data);
					setLoginStatus("mfa_optional");
				} else if (res.data.access_token) {
					completeAuthentication(res.data);
				} else if (res.data.status === "mfa_required") {
					setMfaChallenge(res.data.challenge_id);
					setLoginStatus("mfa_required");
				} else if (res.data.status === "mfa_enrollment_required") {
					setMfaEnrollment(res.data);
					setLoginStatus("mfa_enrollment_required");
				}
			})
			.catch((err) => {
				setAuthError(true);
				setLoginStatus("error");
				console.log(err);
			});
	}

	const startOptionalMfaEnrollment = () => {
		setLoginStatus("mfa_optional_processing");
		setAuthError(false);
		return startTotpEnrollment(mfaOptional.access_token)
			.then((res) => {
				setMfaEnrollment(res.data);
				setLoginStatus("mfa_enrollment_required");
			})
			.catch(() => {
				setAuthError(true);
				setLoginStatus("mfa_optional");
			});
	}

	const skipOptionalMfaEnrollment = () => completeAuthentication(mfaOptional);

	const processMfaCode = (code) => {
		setLoginStatus("mfa_processing");
		setAuthError(false);
		return verifyTotp(mfaChallenge, code)
			.then((res) => completeAuthentication(res.data))
			.catch(() => {
				setAuthError(true);
				setLoginStatus("mfa_required");
			});
	}

	const processMfaEnrollment = (code) => {
		setLoginStatus("mfa_processing");
		setAuthError(false);
		return confirmTotpEnrollment(mfaEnrollment.challenge_id, code)
			.then((res) => completeAuthentication(res.data))
			.catch(() => {
				setAuthError(true);
				setLoginStatus("mfa_enrollment_required");
			});
	}

	const logout = () => {
		authVersionRef.current += 1;
		if (heartBeatRef.current) {
			clearTimeout(heartBeatRef.current);
			heartBeatRef.current = null;
		}
		setAccessToken(null);
		setID(null);
		setName(null);
		setEmail(null);
		setRole(null);
		setLoginStatus("");
		setMfaChallenge(null);
		setMfaEnrollment(null);
		setMfaOptional(null);
		setAuthError(false);
		window.localStorage.clear()
	}

	const authHeartBeat = () => {
		// Hält die Sitzung aktiv, solange ein Refresh-Token vorhanden ist.
		if (heartBeatRef.current) clearTimeout(heartBeatRef.current);
		heartBeatRef.current = setTimeout(async () => {
			heartBeatRef.current = null;
			await validRefresh();
			if (window.localStorage.getItem('refreshToken')) authHeartBeat();
		}, 90000);
	}

	const validRefresh = () => {
		const refreshToken = window.localStorage.getItem('refreshToken');
		const authVersion = authVersionRef.current;
		const pending = refreshRequestRef.current;
		if (pending && pending.token === refreshToken && pending.version === authVersion) {
			return pending.promise;
		}
		const request = { token: refreshToken, version: authVersion };
		request.promise = performRefresh(refreshToken, authVersion).finally(() => {
			if (refreshRequestRef.current === request) refreshRequestRef.current = null;
		});
		refreshRequestRef.current = request;
		return request.promise;
	};

	const performRefresh = async (refreshToken, authVersion) => {
		if (refreshToken) {
			try {
				const res = await axios
					.post(`${process.env.REACT_APP_API_SERVICE_URL}/auth/refresh`, {
						refresh_token: refreshToken
					});
				if (
					res &&
					authVersion === authVersionRef.current &&
					window.localStorage.getItem('refreshToken') === refreshToken
				) {
					if (accessToken !== res.data.access_token)
						setAccessToken(res.data.access_token);
					if (refreshToken !== res.data.refresh_token)
						window.localStorage.setItem('refreshToken', res.data.refresh_token);
					return true;
				}
			} catch (e) {
				return false;
			}
		}
		return false;
	};

	const checkAuthenticated = async () => {
		// console.log("checkAuthenticated");
		// console.log("checkAuthenticated accesstoken", accessToken);
		const authVersion = authVersionRef.current;
		const validRefreshed = await validRefresh();
		if (authVersion !== authVersionRef.current) return false;
		if (accessToken || validRefreshed ) {
			//console.log("checkAuthenticated validRefresh OK", email, accessToken,validRefreshed );

			setLoginStatus("success")
			return true;
		}
		setLoginStatus("")
		return false;
	};

	const getUserStatus = (isCurrent) => {
    const options = {
      url: `${process.env.REACT_APP_API_SERVICE_URL}/auth/status`,
      method: 'get',
      headers: getRequestAuthHeader()
		};
		
    return axios(options)
			.then((res) => {
				if (!isCurrent()) return;
				if (!res.data.id || !["administrator", "agent", "tippgeber", "backoffice"].includes(res.data.role)) {
					throw new Error("Ungültiges Benutzerprofil");
				}
				setID(res.data.id);
				setEmail(res.data.email);
				setName(res.data.name);
				setRole(res.data.role);
				authHeartBeat();
			})
			.catch(() => {
				if (!isCurrent()) return;
				setID(null);
				setRole(null);
				setAuthError(true);
				setLoginStatus("error");
			});
	};
	

	const getRequestAuthHeader = (json = true) => {
		const headers = {
			Authorization: `Bearer ${accessToken}`
		};
		if (json) headers['Content-Type'] = 'application/json';
		return headers;
	}
	// if (!token) {
	// 	if (ID) {
	// 		setID(null);
	// 		setName(null);
	// 		setEmail(null);
	// 		setDownloadNonce(null);
	// 		setRole(null);
	// 	}
	// } else {
	// 	if (loading) {
	// 		if (!isLoading) setIsLoading(true);
	// 	} else {
	// 		if (error) {
	// 			if (isLoading) setIsLoading(false);
	// 			if (ID) {
	// 				setID(null);
	// 				setName(null);
	// 				setEmail(null);
	// 				setRole(null);
	// 			}
	// 			//return <div>Error! {error.message}</div>;
	// 		}

	// 		if (data && data.viewer && data.viewer.id) {
	// 			if (ID !== data.viewer.id) {
	// 				if (isLoading) setIsLoading(false);

	// 				setID(data.viewer.id);
	// 				setName(data.viewer.username);
	// 				setEmail(data.viewer.email);
	// 				setRole(data.viewer.role);
	// 			}
	// 		} else {
	// 			if (isLoading) setIsLoading(false);
	// 			if (ID) {
	// 				setID(null);
	// 				setName(null);
	// 				setEmail(null);
	// 				setDownloadNonce(null);
	// 				setRole(null);
	// 			}
	// 		}
	// 	}
	// }

	return (
		<UserContext.Provider
			value={{
				user: {
					checkAuthenticated: checkAuthenticated,
					ID: ID,
					name: name,
					role: role,
					email: email,
				},
				processLogin: processLogin,
				processMfaCode: processMfaCode,
				processMfaEnrollment: processMfaEnrollment,
				completeOptionalMfaEnrollment: completeAuthentication,
				mfaEnrollment: mfaEnrollment,
				mfaOptional: mfaOptional,
				startOptionalMfaEnrollment: startOptionalMfaEnrollment,
				skipOptionalMfaEnrollment: skipOptionalMfaEnrollment,
				authError: authError,
				logout: logout,
				loginStatus: loginStatus,
				setLoginStatus: setLoginStatus,
				getRequestAuthHeader: getRequestAuthHeader,
				accessToken: accessToken,
				refreshAuthenticationAndTryAgain: validRefresh
			}}
		>
			{props.children}
		</UserContext.Provider>
	);
}

export default UserContextProvider;
